import {
  Button,
  Flex,
  Title,
  Loader,
  Skeleton,
  Text,
  Stack,
} from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { useContext, useState } from 'react';
import { saveAs } from 'file-saver';
import { useNotification } from 'context/NotificationContext';
import ReportContext from 'context/ReportContext';
import { formatPhoneNumber } from 'utils/phone';
import { useDeviceType } from 'hooks/useDeviceType';
import { useNavigate } from 'react-router-dom';

import classes from './index.module.scss';
import RecordPlayer from './RecordPlayer';
import Overlay from 'components/Overlay';
import AuthContext from 'context/AuthContext';
import routes from 'utils/routes';

const Recording = () => {
  const { showSuccess, showError } = useNotification();
  const {
    data: { callDetails, isLoadingCallDetails },
  } = useContext(ReportContext);
  const {
    data: { company },
  } = useContext(AuthContext);
  const { isMobile } = useDeviceType();
  const [isDownloading, setIsDownloading] = useState(false);
  const navigate = useNavigate();
  const audioUrl = callDetails.audio_recording_filepath;
  const phoneNumber = callDetails.participant?.phone_number;
  const wasDisabled = audioUrl === 'Recording Disabled';
  const isDeleted = !audioUrl;
  const isUnavailable = !isLoadingCallDetails && (wasDisabled || isDeleted);
  const disableRecording =
    isDownloading || isLoadingCallDetails || isUnavailable;

  const handleDownloadRecording = async () => {
    if (isUnavailable) {
      return;
    }
    try {
      setIsDownloading(true);
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `recording_${phoneNumber}_${timestamp}.mp3`;

      saveAs(blob, filename);
      showSuccess('Recording downloaded successfully');
    } catch (error) {
      console.error('Error downloading recording:', error);
      showError('Failed to download recording. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Flex
        justify="space-between"
        w="100%"
        align="center"
        mb={{ base: 0, md: 'sm' }}
      >
        {phoneNumber ? (
          <Title size="md" fw={600} className={classes.phoneNumber}>
            {formatPhoneNumber(phoneNumber)}
          </Title>
        ) : (
          <Skeleton w={125} h={20} />
        )}
        <Button
          variant="transparent"
          size="sm"
          fw={400}
          p={0}
          disabled={disableRecording}
          onClick={handleDownloadRecording}
          style={{
            opacity: disableRecording ? 0.6 : 1,
          }}
          bg="none"
          rightSection={
            isDownloading ? (
              <Loader size="0.75rem" style={{ marginLeft: '0.7rem' }} />
            ) : (
              <IconDownload
                size={19}
                stroke={1.5}
                style={{ marginLeft: '0.25rem', marginBottom: '0.2rem' }}
              />
            )
          }
        >
          Download {isMobile ? '' : 'Recording'}
        </Button>
      </Flex>
      <Overlay
        showOverlay={isUnavailable}
        w="100%"
        blur={8}
        overlayContent={
          <Stack align="center" justify="center" h="100%" gap="sm" p="md">
            <Text size="sm" fw={500} ta="center">
              {isDeleted
                ? 'Recording no longer available'
                : company?.record_audio
                  ? 'No recording available. Recording was turned off at the time of this call.'
                  : 'No recording available. Recording is currently turned off in your settings'}
            </Text>
            {wasDisabled && !company?.record_audio && (
              <Button
                variant="outline"
                size="xs"
                onClick={() => {
                  navigate(`${routes.settingsPath.href}/?tab=general`);
                }}
              >
                Go to settings
              </Button>
            )}
          </Stack>
        }
      >
        <RecordPlayer isUnavailable={isUnavailable} />
      </Overlay>
    </>
  );
};

export default Recording;
